/**
 * Derive Kuali dashboard-field choices from the unsaved module selection.
 *
 * cm-coding: The connector form must preview available fields before a feed
 * exists, so it mirrors the provider catalog locally for immediate editing.
 * The backend remains authoritative and validates the submitted ordered keys;
 * this helper never controls persisted identity, routing, or synchronization.
 */
import type { ConnectorSourceDisplayFieldDefinition } from '../../types'
import type { KualiResearchModuleOptions } from '../types'

const KUALI_SOURCE_DISPLAY_FIELDS: ConnectorSourceDisplayFieldDefinition[] = [
  { key: 'title', label: 'Title', default: true },
  { key: 'principalInvestigator', label: 'PI', default: true },
  { key: 'leadUnit', label: 'Lead unit', default: false },
  { key: 'sponsor', label: 'Sponsor', default: false },
  { key: 'status', label: 'Status', default: false },
  { key: 'requisitionerUnit', label: 'Requisitioner unit', default: true },
]

export function kualiSourceDisplayFields(
  modules: KualiResearchModuleOptions[],
): ConnectorSourceDisplayFieldDefinition[] {
  const available = new Set(['title'])
  const defaults = new Set(['title'])
  for (const module of modules) {
    const awardAnchored = module.source === 'awards'
      || module.scope === 'related-all-awards'
      || module.scope === 'related-selected-awards'
    if (awardAnchored) {
      const awardFields = ['principalInvestigator', 'leadUnit', 'sponsor', 'status']
      awardFields.forEach(key => available.add(key))
      defaults.add('principalInvestigator')
    } else if (module.source === 'subawards') {
      available.add('requisitionerUnit')
      defaults.add('requisitionerUnit')
    } else if (module.source === 'negotiations') {
      available.add('leadUnit')
      defaults.add('leadUnit')
    }
  }
  return KUALI_SOURCE_DISPLAY_FIELDS
    .filter(field => available.has(field.key))
    .map(field => ({ ...field, default: defaults.has(field.key) }))
}

export const defaultKualiSourceDisplayFieldKeys = (
  modules: KualiResearchModuleOptions[],
) => kualiSourceDisplayFields(modules)
  .filter(field => field.default)
  .map(field => field.key)
